import { cn } from '@/shared/helpers/classnames'
import { type IconProps, WithIcon } from '@/shared/ui/icon'
import { type TooltipProps, useToolTip } from '@/shared/ui/tooltip'

import { type FC } from 'react'

export type BadgeVariant = 'default' | 'soft' | 'dot'

export type BadgeType = 'success' | 'warning' | 'error' | 'info' | 'neutral'

/**
 * Props pour le composant Badge
 */
export type BadgeProps = {
  /** Type de badge qui détermine la couleur */
  type: BadgeType
  /** Type de badge qui détermine la couleur */
  variant?: BadgeVariant
  /** Label personnalisé à afficher */
  label: string
  /** Optional icon */
  icon?: IconProps
  /** Optional tooltip */
  tooltip?: TooltipProps
}

export const Badge: FC<BadgeProps> = ({
  type,
  label,
  icon,
  variant = 'default',
  tooltip,
  ...props
}) => {
  const tooltipProps = useToolTip({ ...tooltip })
  const iconNode = useIcon({ ...icon, type, variant })

  return (
    <div
      {...tooltipProps}
      className={cn('badge-sm', {
        badge: variant !== 'dot',
        'flex items-center gap-2': variant === 'dot',
        'badge-soft rounded-full font-medium': variant === 'soft',
        'rounded-full font-medium': variant === 'default',
        'badge-neutral': type === 'neutral' && variant !== 'dot',
        'badge-success': type === 'success' && variant !== 'dot',
        'badge-warning': type === 'warning' && variant !== 'dot',
        'badge-error': type === 'error' && variant !== 'dot',
        'badge-info': type === 'info' && variant !== 'dot',
      })}
      {...props}
    >
      <WithIcon position={icon?.position} icon={iconNode}>
        {label}
      </WithIcon>
    </div>
  )
}

const useIcon = ({
  component: IconComponent,
  variant,
  type,
}: IconProps & { variant: BadgeVariant; type: BadgeType }) => {
  if (variant === 'dot') {
    return <DotStatut type={type} />
  }

  return IconComponent ? <IconComponent size={14} /> : null
}

const DotStatut = ({ type }: { type: BadgeType }) => {
  return (
    <div
      className={cn('h-2 w-2 -translate-y-px rounded-full', {
        'bg-success': type === 'success',
        'bg-warning': type === 'warning',
        'bg-error': type === 'error',
        'bg-info': type === 'info',
        'bg-neutral': type === 'neutral',
      })}
    />
  )
}
